var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { useCallback, useEffect, useMemo, useState } from "react";
import { selectors, useAppSelector } from "store";
import { useTimeDataMinMaxDomain } from "hooks/graph-panel/graph";
var Y_DOMAIN_OFFSET = 5;
export var getZoomedDomain = function (data) {
    var _a = __read(data.reduce(function (acc, dataPoint) {
        Object.entries(dataPoint).forEach(function (_a) {
            var _b = __read(_a, 2), id = _b[0], dataValue = _b[1];
            if (id !== "time") {
                acc[0] = Math.min(dataValue, acc[0]);
                acc[1] = Math.max(dataValue, acc[1]);
            }
        });
        return acc;
    }, [Infinity, -Infinity]), 2), min = _a[0], max = _a[1];
    return [min - Y_DOMAIN_OFFSET, max + Y_DOMAIN_OFFSET];
};
export var findClosestIndex = function (timeData, value) {
    var closestIndex = 0;
    var smallestDiff = Math.abs(timeData[0] - value);
    for (var i = 1; i < timeData.length; i++) {
        var diff = Math.abs(timeData[i] - value);
        if (diff < smallestDiff) {
            smallestDiff = diff;
            closestIndex = i;
        }
    }
    return closestIndex;
};
export var useZoom = function () {
    var _a = __read(useState(), 2), zoomedDomains = _a[0], setZoomedDomains = _a[1];
    var graphData = useAppSelector(selectors.selectGraphData);
    var timeData = graphData.systemProperty.timeData;
    var xAxisDomain = useTimeDataMinMaxDomain();
    var parsedGraphData = useAppSelector(selectors.selectParsedGraphData);
    var vacuumPumpData = useMemo(function () { return (parsedGraphData ? parsedGraphData.nodeData : []); }, [parsedGraphData]);
    var robotMotionData = useMemo(function () { return (parsedGraphData ? parsedGraphData.robotMotionData : []); }, [parsedGraphData]);
    var airConsumptionData = useMemo(function () { return (parsedGraphData ? parsedGraphData.airConsumptionData : []); }, [parsedGraphData]);
    var liftForceData = useMemo(function () { return (parsedGraphData ? parsedGraphData.liftingForcesData : []); }, [parsedGraphData]);
    var isPerformanceDataValid = useAppSelector(selectors.selectIsPerformanceDataValid);
    var resetZoom = useCallback(function () {
        setZoomedDomains(undefined);
    }, []);
    // A parameter change invalidates the current results and queues a fresh
    // calculation. The zoom domain was chosen against the old timeline, so it no
    // longer maps to anything meaningful — reset it so the graphs return to the
    // full view until the user zooms into the recalculated data.
    useEffect(function () {
        if (!isPerformanceDataValid) {
            resetZoom();
        }
    }, [isPerformanceDataValid, resetZoom]);
    var zoom = useCallback(function (zoomTimeAreaStart, zoomTimeAreaEnd) {
        if (!timeData.length) {
            console.warn("Cannot zoom. No graph time data available");
            return;
        }
        if (zoomTimeAreaStart >= zoomTimeAreaEnd) {
            console.warn("Cannot zoom. Given zoom area is 0 or negative");
            return;
        }
        var _a = __read(xAxisDomain, 2), timeDomainStart = _a[0], timeDomainEnd = _a[1];
        var clampedZoomTimeAreaStart = Math.max(timeDomainStart, zoomTimeAreaStart);
        var clampedZoomTimeAreaEnd = Math.min(timeDomainEnd, zoomTimeAreaEnd);
        if (clampedZoomTimeAreaStart >= clampedZoomTimeAreaEnd) {
            console.warn("Cannot zoom. Given zoom area is outside the time domain");
            return;
        }
        var dataIndexLeft = findClosestIndex(timeData, clampedZoomTimeAreaStart);
        var dataIndexRight = findClosestIndex(timeData, clampedZoomTimeAreaEnd);
        // dataIndexRight must be larger than dataIndexLeft
        if (dataIndexLeft === dataIndexRight) {
            if (dataIndexRight < timeData.length - 1) {
                dataIndexRight++;
            }
            else if (dataIndexLeft > 0) {
                dataIndexLeft--;
            }
            else {
                console.warn("Cannot zoom, couldn't find suitable data index range.");
            }
        }
        var slice = function (data) {
            return data.slice(Math.max(dataIndexLeft - 1, 0), dataIndexRight);
        };
        setZoomedDomains({
            vacuumLevel: getZoomedDomain(slice(vacuumPumpData)),
            robotMotion: getZoomedDomain(slice(robotMotionData)),
            airConsumption: getZoomedDomain(slice(airConsumptionData)),
            liftForce: getZoomedDomain(slice(liftForceData)),
            time: [clampedZoomTimeAreaStart, clampedZoomTimeAreaEnd],
        });
    }, [
        timeData,
        xAxisDomain,
        vacuumPumpData,
        robotMotionData,
        airConsumptionData,
        liftForceData,
    ]);
    return {
        zoom: zoom,
        zoomOut: resetZoom,
        zoomedVacuumLevelDomain: zoomedDomains === null || zoomedDomains === void 0 ? void 0 : zoomedDomains.vacuumLevel,
        zoomedRobotMotionDomain: zoomedDomains === null || zoomedDomains === void 0 ? void 0 : zoomedDomains.robotMotion,
        zoomedAirConsumptionDomain: zoomedDomains === null || zoomedDomains === void 0 ? void 0 : zoomedDomains.airConsumption,
        zoomedLiftForceDomain: zoomedDomains === null || zoomedDomains === void 0 ? void 0 : zoomedDomains.liftForce,
        zoomedTimeDomain: zoomedDomains === null || zoomedDomains === void 0 ? void 0 : zoomedDomains.time,
    };
};
